import * as Immutable from "immutable";
import CommentModel from "./CommentModel";
import RatingModel from "./RatingModel";
import UserModel from "./UserModel";

export default class DiscussionModel extends Immutable.Record({
    project: null,
    discussion: 0,
    sort: "rating",
    total: 0,
    loading: false,
    display: false,
    autofocus: true,
    form_value: "",
    form_sending: false,
    premium: false,
    locale: "sk",
    badges: false,

    comments: Immutable.OrderedMap<number, CommentModel>(),
    ratings: Immutable.Map<number, RatingModel>(),
    expands: Immutable.Map<number, boolean>(),
    authors: Immutable.Set<number>(),
    user: null,
    login: "/login",
    verify: "/overenie",
    admin: false,

    delete_modal: false,
    delete_comment: 0,
    modal_error: false,
    modal_error_type: 0,
    modal_login: false,
    modal_login_type: 0,
    modal_premium: false,
    modal_verify: false,
    report_modal: false,
    report_comment: 0,
    report_result_modal: false,
    block_modal: false,
    block_comment: 0,
}) {

    public isAdmin(): boolean {
        return this.get("admin");
    }

    public isExpaned(commentId: number): boolean {
        return this.get("expands").get(commentId, false);
    }

    public getAuthors(): Immutable.Set<number> {
        return this.get("authors");
    }

    public getReportModal(): boolean {
        return this.get("report_modal");
    }

    public getReportResultModal(): boolean {
        return this.get("report_result_modal");
    }

    public getReportComment(): number {
        return this.get("report_comment");
    }

    public clearComments(): DiscussionModel {
        return this.set("comments", Immutable.OrderedMap()) as DiscussionModel;
    }

    public getComment(commentId: number): CommentModel | null {
        return this.get("comments").get(commentId);
    }

    public getComments(): Immutable.OrderedMap<number, CommentModel> {
        return this.get("comments");
    }

    public setComments(comments: Immutable.OrderedMap<number, CommentModel>): DiscussionModel {
        return this.set("comments", comments) as DiscussionModel;
    }

    public setReplyValue(comment: number, value: string): DiscussionModel {
        return this.updateIn(["comments", comment], (comment: CommentModel) => {
            return comment.setReplyValue(value);
        }) as DiscussionModel;
    }

    public setReplyDisplay(comment: number, display: boolean): DiscussionModel {
        return this.updateIn(["comments", comment], (comment: CommentModel) => {
            return comment.setReplyDisplay(display);
        }) as DiscussionModel;
    }

    public getUser(): UserModel | null {
        return this.get("user");
    }

    public setUser(user: UserModel | null): DiscussionModel {
        return this.set("user", user) as DiscussionModel;
    }

    public getLogin(): string {
        return this.get("login");
    }

    public setLogin(login: string): DiscussionModel {
        return this.set("login", login) as DiscussionModel;
    }

    public getVerify(): string {
        return this.get("verify");
    }

    public setVerify(verify: string): DiscussionModel {
        return this.set("verify", verify) as DiscussionModel;
    }

    public getRating(comment: number): RatingModel | null {
        return this.get("ratings").get(comment);
    }

    public setRatings(ratings: Immutable.List<RatingModel>): DiscussionModel {
        return this.set("ratings", ratings.reduce((r, v) => {
            return r.set(v.getCommentId(), v);
        }, Immutable.Map<number, RatingModel>())) as DiscussionModel;
    }

    public getProject(): number | null {
        return this.get("project");
    }

    public setProject(project: number | null): DiscussionModel {
        return this.set("project", project) as DiscussionModel;
    }

    public getBadges(): boolean {
        return this.get("badges");
    }

    public setBadges(badges: boolean): DiscussionModel {
        return this.set("badges", badges) as DiscussionModel;
    }

    public getDiscussion(): number {
        return this.get("discussion");
    }

    public setDiscussion(discussion: number): DiscussionModel {
        return this.set("discussion", discussion) as DiscussionModel;
    }

    public getSort(): string {
        return this.get("sort");
    }

    public setSort(sort: string): DiscussionModel {
        return this.set("sort", sort) as DiscussionModel;
    }

    // public getComments(): Immutable.List<CommentModel> {
    //     return this.get("comments");
    // }
    //
    // public setComments(comments: Immutable.List<CommentModel>): DiscussionModel {
    //     return this.set("comments", comments) as DiscussionModel;
    // }

    public isLoading(): boolean {
        return this.get("loading");
    }

    public setLoading(loading: boolean): DiscussionModel {
        return this.set("loading", loading) as DiscussionModel;
    }

    public getTotal(): number {
        return this.get("total");
    }

    public setTotal(total: number): DiscussionModel {
        return this.set("total", total) as DiscussionModel;
    }

    public getDisplay(): boolean {
        return this.get("display");
    }

    public setDisplay(display: boolean): DiscussionModel {
        return this.set("display", display) as DiscussionModel;
    }

    public getAutofocus(): boolean {
        return this.get("autofocus");
    }

    public setAutofocus(autofocus: boolean): DiscussionModel {
        return this.set("autofocus", autofocus) as DiscussionModel;
    }

    public getFormValue(): string {
        return this.get("form_value");
    }

    public setFormValue(value: string): DiscussionModel {
        return this.set("form_value", value) as DiscussionModel;
    }

    public getDeleteModal(): boolean {
        return this.get("delete_modal");
    }

    public getDeleteComment(): number {
        return this.get("delete_comment");
    }

    public getModalLogin(): boolean {
        return this.get("modal_login");
    }

    public getModalLoginType(): number {
        return this.get("modal_login_type");
    }

    public showModalLogin(type: number): DiscussionModel {
        return this
            .set("modal_login", true)
            .set("modal_login_type", type) as DiscussionModel;
    }

    public hideModalLogin(): DiscussionModel {
        return this
            .set("modal_login", false)
            .set("modal_login_type", 0) as DiscussionModel;
    }

    public getModalError(): boolean {
        return this.get("modal_error");
    }

    public getModalErrorType(): number {
        return this.get("modal_error_type");
    }

    public showModalError(type: number): DiscussionModel {
        return this
            .set("modal_error", true)
            .set("modal_error_type", type) as DiscussionModel;
    }

    public hideModalError(): DiscussionModel {
        return this
            .set("modal_error", false)
            .set("modal_error_type", 0) as DiscussionModel;
    }

    public getBlockModal(): boolean {
        return this.get("block_modal");
    }

    public getBlockComment(): number {
        return this.get("block_comment");
    }

    public isFormSending(): boolean {
        return this.get('form_sending');
    }

    public isPremium(): boolean {
        return this.get('premium');
    }

    public setPremium(premium: boolean): DiscussionModel {
        return this.set("premium", premium) as DiscussionModel;
    }

    public getModalPremium(): boolean {
        return this.get("modal_premium");
    }

    public showModalPremium(): DiscussionModel {
        return this.set("modal_premium", true) as DiscussionModel;
    }

    public hideModalPremium(): DiscussionModel {
        return this.set("modal_premium", false) as DiscussionModel;
    }

    public getModalVerify(): boolean {
        return this.get("modal_verify");
    }

    public showModalVerify(): DiscussionModel {
        return this.set("modal_verify", true) as DiscussionModel;
    }

    public hideModalVerify(): DiscussionModel {
        return this.set("modal_verify", false) as DiscussionModel;
    }

    public getLocale(): string {
        return this.get('locale');
    }

    public setLocale(locale: string): DiscussionModel {
        return this.set("locale", locale) as DiscussionModel;
    }

}